import { Badge } from "@/components/ui/badge";
import SentimentBadge from "./SentimentBadge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Clock, FileText, TrendingUp, Minus, TrendingDown } from "lucide-react";
import React from "react";
import { AiAgentCall, CallSentiment, parseCallSummary } from "@/model/interface/AiAgent";
import CallDirectionBadge from "./CallDirectionBadge";

interface CallHistoryRowProps {
  call: AiAgentCall;
  onOpenTranscript: (callId: string) => void;
}

const OUTCOME_VARIANTS: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  QUALIFIED: "default",
  NOT_QUALIFIED: "secondary",
  ESCALATED: "outline",
  APPOINTMENT_SET: "default",
  CALLBACK_REQUESTED: "outline",
  VOICEMAIL: "secondary",
  HANGUP: "destructive",
  ERROR: "destructive",
  DO_NOT_CONTACT: "destructive",
};


function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) {
    return "-";
  }
  return new Date(dateStr).toLocaleString();
}

function formatCost(cost: number | null | undefined): string {
  if (cost == null) {
    return "-";
  }
  return `$${Number(cost).toFixed(4)}`;
}

export default function CallHistoryRow({ call, onOpenTranscript }: CallHistoryRowProps) {
  const outcomeVariant = OUTCOME_VARIANTS[call.outcome] ?? "secondary";
  const summary = parseCallSummary(call.summary);
  const sentiment = call.sentiment ?? summary?.sentiment;
  const contactName = [call.contact?.firstName, call.contact?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <TableRow>
      <TableCell>{formatDate(call.startedAt)}</TableCell>
      <TableCell>
        <CallDirectionBadge direction={call.call?.direction} />
      </TableCell>
      <TableCell>
        {contactName ? (
          <div className="flex flex-col">
            <span className="truncate">{contactName}</span>
            {call.call?.phoneNumber && (
              <span className="text-xs text-muted-foreground">{call.call.phoneNumber}</span>
            )}
          </div>
        ) : (
          <span className={call.call?.phoneNumber ? "" : "text-muted-foreground"}>
            {call.call?.phoneNumber ?? "—"}
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-muted-foreground" />
          {formatDuration(call.durationSeconds)}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={outcomeVariant}>{call.outcome.replace(/_/g, " ")}</Badge>
      </TableCell>
      <TableCell>
        {sentiment ? (
          <SentimentBadge sentiment={sentiment} />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="max-w-xs">
        {summary ? (
          <p className="text-xs text-muted-foreground line-clamp-2">{summary.callSummary}</p>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>{formatCost(call.cost)}</TableCell>
      <TableCell>
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => onOpenTranscript(call.id)}>
          <FileText className="h-3.5 w-3.5" />
          Details
        </Button>
      </TableCell>
    </TableRow>
  );
}
